import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  HostingFloor,
  PlanCatalog,
  PlanCatalogViewModel,
  ServiceCatalog,
  type Plan,
  type PlanKey,
  type ServiceKey,
} from "@lumora/core";
import { useViewModel } from "@lumora/ui";
import { client, SITE } from "../client.js";

/** Build query string for /get-started prefill (#656). */
export function servicesGetStartedPath(
  build: ServiceKey | null,
  host: PlanKey | null,
  addons: ReadonlySet<string>,
): string {
  const params = new URLSearchParams();
  if (build) params.set("service", build);
  if (host) params.set("package", host);
  if (addons.size > 0) params.set("addons", [...addons].join(","));
  const q = params.toString();
  return q ? `/get-started?${q}` : "/get-started";
}

function PlanCard({
  plan,
  selected,
  disabled,
  lockedReason,
  onSelect,
}: {
  plan: Plan;
  selected: boolean;
  disabled: boolean;
  lockedReason: string | null;
  onSelect: () => void;
}) {
  const orderCut = PlanCatalog.orderCutForPlan(plan.key);
  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      data-lumora-plan={plan.key}
      data-recommended={plan.recommended ? "true" : undefined}
      data-selected={selected ? "true" : undefined}
      data-disabled={disabled ? "true" : undefined}
      aria-disabled={disabled || undefined}
      onClick={() => {
        if (!disabled) onSelect();
      }}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect();
        }
      }}
    >
      <h2>{plan.name}</h2>
      <p data-lumora-plan-price>{PlanCatalog.marketingPrice(plan)}</p>
      <p data-lumora-plan-blurb>{PlanCatalog.marketingNote(plan.key) || plan.blurb}</p>
      <div data-lumora-plan-details>
        <p data-lumora-plan-details-title>Includes</p>
        <ul data-lumora-plan-includes>
          {PlanCatalog.marketingIncludes(plan).map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
        {orderCut ? (
          <>
            <p data-lumora-plan-details-title>Online orders</p>
            <ul data-lumora-plan-includes>
              <li>{orderCut}</li>
              <li>
                Card processing fees are charged separately by the payment company and are not
                included in Lumora's platform fee on online orders.
              </li>
            </ul>
          </>
        ) : null}
      </div>
      {plan.recommended ? <span data-lumora-recommended>Recommended</span> : null}
      {disabled && lockedReason ? <span data-lumora-plan-locked>{lockedReason}</span> : null}
    </div>
  );
}

/**
 * Public pricing — plain language, build-gated add-ons (#658, #659).
 */
export function PricingPage({ className = "site-packages site-packages--wide" }: { className?: string }) {
  const [build, setBuild] = useState<ServiceKey | null>(null);
  const [host, setHost] = useState<PlanKey | null>(null);
  const [addons, setAddons] = useState<ReadonlySet<string>>(() => new Set());
  const floors = useMemo(() => new HostingFloor(), []);
  const catalogVM = useMemo(() => new PlanCatalogViewModel(client, SITE), []);
  const catalog = useViewModel(catalogVM);

  useEffect(() => {
    void catalogVM.load();
  }, [catalogVM]);

  const tiers = ServiceCatalog.buildTiers();
  const foundation =
    catalog.plans.find((p) => p.key === "foundation") ?? PlanCatalog.byKey("foundation");
  const backendPlans = useMemo(() => {
    const wanted = new Set(["payg", "starter", "growth", "pro"]);
    const loaded = catalog.plans.filter((p) => wanted.has(p.key));
    return loaded.length >= 4 ? loaded : PlanCatalog.backendHostingPlans();
  }, [catalog.plans]);
  const copy = ServiceCatalog.sectionCopy;
  const visibleAddons = ServiceCatalog.marketingAddons(build);

  const foundationDisabled = floors.isDisabled("foundation", build, addons);
  const floorRank = floors.requiredRank(addons, build);

  useEffect(() => {
    setAddons((prev) => {
      const next = ServiceCatalog.pruneAddonSelection(prev, build);
      return next.size === prev.size && [...prev].every((k) => next.has(k)) ? prev : next;
    });
  }, [build]);

  useEffect(() => {
    setAddons((prev) => {
      const next = ServiceCatalog.pruneAddonsForHost(prev, build, host);
      return next.size === prev.size && [...prev].every((k) => next.has(k)) ? prev : next;
    });
  }, [host, build]);

  useEffect(() => {
    if (host && floors.isDisabled(host, build, addons)) {
      setHost(null);
    }
  }, [build, addons, host, floors]);

  const hostingHint = useMemo(() => {
    // Only explain the *current* selection state — never a stale Foundation note.
    if (host === "foundation" && build && HostingFloor.buildAllowsFoundation(build)) {
      return {
        title: "Brochure hosting",
        body: "Foundation covers your domain and email. You do not need Pay as you go unless you later add online ordering or another live channel.",
      };
    }
    if (floorRank > 0) {
      const label = HostingFloor.floorLabel(floorRank);
      return {
        title: `Hosting needs ${label} or higher`,
        body: "An add-on you picked raised the minimum plan. Grey plans are unavailable until you change add-ons or pick a higher plan.",
      };
    }
    if (build && !HostingFloor.buildAllowsFoundation(build)) {
      return {
        title: "This build needs the Lumora API",
        body: "Connect, Commerce, and Custom run on Pay as you go or higher. Foundation is only for brochure sites (Spark or Brand).",
      };
    }
    return null;
  }, [build, host, floorRank]);

  const lockedReasonFor = (plan: PlanKey): string | null => {
    if (!floors.isDisabled(plan, build, addons)) return null;
    if (plan === "foundation") return "Brochure sites only";
    if (floorRank > 0) return `Needs ${HostingFloor.floorLabel(floorRank)}+`;
    return "Unavailable";
  };

  const ctaTo = servicesGetStartedPath(build, host, addons);

  return (
    <section className={className} data-lumora-view="pricing">
      <h1>Pricing</h1>
      <p data-lumora-lead>
        Pick a build, then any extras, then hosting. Nothing is billed on this page — Get started
        carries your picks into a quote.
      </p>

      <h2 className="site-packages-heading">Your data &amp; your site</h2>
      <p className="site-packages-section-lead">{copy.portability}</p>
      <ul data-lumora-disclosures>
        {ServiceCatalog.disclosures.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>

      <h2 className="site-packages-heading">Build</h2>
      <p className="site-packages-section-lead">{copy.build}</p>
      <div data-lumora-plans data-lumora-plans-fill>
        {tiers.map((tier) => {
          const selected = build === tier.key;
          return (
            <div
              key={tier.key}
              role="button"
              tabIndex={0}
              data-lumora-plan={tier.key}
              data-recommended={tier.recommended ? "true" : undefined}
              data-selected={selected ? "true" : undefined}
              onClick={() => setBuild(tier.key)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setBuild(tier.key);
                }
              }}
            >
              <h2>{tier.name}</h2>
              <p data-lumora-plan-price>{tier.buildRange}</p>
              <p data-lumora-plan-blurb>{tier.blurb}</p>
              <div data-lumora-plan-details>
                <p data-lumora-plan-details-title>Includes</p>
                <ul data-lumora-plan-includes>
                  {tier.includes.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                {tier.excludes.length > 0 ? (
                  <>
                    <p data-lumora-plan-details-title>Not included</p>
                    <ul data-lumora-plan-excludes>
                      {tier.excludes.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </>
                ) : null}
              </div>
              {tier.recommended ? <span data-lumora-recommended>Recommended</span> : null}
            </div>
          );
        })}
      </div>

      <h2 className="site-packages-heading">Add-ons</h2>
      <p className="site-packages-section-lead">{copy.addons}</p>
      {!build ? (
        <p className="site-packages-hint site-packages-hint--neutral">
          Choose a build first. Spark and Brand have no add-ons; Connect and above unlock extras.
        </p>
      ) : visibleAddons.length === 0 ? (
        <p className="site-packages-hint site-packages-hint--neutral">
          {ServiceCatalog.byKey(build).name} is a brochure build — no add-ons on this plan.
        </p>
      ) : (
        <div data-lumora-plans data-lumora-plans-fill data-lumora-plans-compact>
          {visibleAddons.map((addon) => {
            const selected = addons.has(addon.key);
            const lock = ServiceCatalog.addonLockReason(addon.key, build, host);
            const disabled = Boolean(lock);
            return (
              <div
                key={addon.key}
                role="button"
                tabIndex={disabled ? -1 : 0}
                data-lumora-plan={addon.key}
                data-selected={selected ? "true" : undefined}
                data-disabled={disabled ? "true" : undefined}
                aria-disabled={disabled || undefined}
                onClick={() => {
                  if (disabled) return;
                  setAddons((prev) => ServiceCatalog.toggleAddonSelection(prev, addon.key, build));
                }}
                onKeyDown={(e) => {
                  if (disabled) return;
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setAddons((prev) => ServiceCatalog.toggleAddonSelection(prev, addon.key, build));
                  }
                }}
              >
                <h2>{addon.name}</h2>
                <p data-lumora-plan-price>{addon.minPlan}</p>
                <p data-lumora-plan-blurb>{addon.blurb}</p>
                {disabled && lock ? <span data-lumora-plan-locked>{lock}</span> : null}
              </div>
            );
          })}
        </div>
      )}

      <h2 className="site-packages-heading">Hosting</h2>
      <p className="site-packages-section-lead">{copy.hosting}</p>
      <div className="hosting-split" data-lumora-hosting-split>
        <div data-lumora-plans data-lumora-plans-fill data-lumora-hosting-static>
          <PlanCard
            plan={foundation}
            selected={host === foundation.key}
            disabled={foundationDisabled}
            lockedReason={lockedReasonFor(foundation.key)}
            onSelect={() => setHost(foundation.key)}
          />
        </div>
        <div className="hosting-split-bar" aria-hidden="true" />
        <div data-lumora-plans data-lumora-plans-fill data-lumora-hosting-backend>
          {backendPlans.map((plan) => (
            <PlanCard
              key={plan.key}
              plan={plan}
              selected={host === plan.key}
              disabled={floors.isDisabled(plan.key, build, addons)}
              lockedReason={lockedReasonFor(plan.key)}
              onSelect={() => setHost(plan.key)}
            />
          ))}
        </div>
      </div>
      {hostingHint ? (
        <aside className="site-packages-callout" data-lumora-hosting-callout>
          <p className="site-packages-callout-title">{hostingHint.title}</p>
          <p className="site-packages-callout-body">{hostingHint.body}</p>
        </aside>
      ) : null}

      <div data-lumora-checkout className="services-cta">
        <div className="services-cta-copy">
          <p className="services-cta-kicker">Next step</p>
          <p className="services-cta-title">Ready for a quote?</p>
          <p className="services-cta-lead">
            We open Get started with your selections filled in. Nothing is charged until we send a
            billing link.
          </p>
          {(build || host || addons.size > 0) && (
            <p className="services-cta-picks">
              {[
                build ? ServiceCatalog.byKey(build).name : null,
                host ? PlanCatalog.byKey(host).name : null,
                addons.size > 0 ? `${addons.size} add-on${addons.size === 1 ? "" : "s"}` : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </p>
          )}
        </div>
        <Link to={ctaTo} className="lm-btn lm-btn-primary services-cta-btn">
          Get started
        </Link>
      </div>
    </section>
  );
}
